import { useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import type { SkillSuggestion } from '@/modules/jobs/signal/types';
import { type VerdictTone, verdictTone } from '@/modules/jobs/signal/verdict';
import { useDebouncedValue } from '@/modules/search/hooks/useDebouncedValue';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/modules/ui/components/command/Command';

import { TONE_DOT } from './tone';

/** Server results are worthless below this length; also avoids a request per single keystroke. */
const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;

interface SkillTypeaheadProps {
  /** Suggestions for the current (debounced) query — supplied by the container's search hook. */
  results: SkillSuggestion[];
  isLoading?: boolean;
  /**
   * Fired with the debounced, trimmed query once it reaches {@link MIN_QUERY} chars (and with `''` when it drops
   * back below that, so the container can clear results). The container owns the actual `/skill-search` call.
   */
  onQueryChange: (query: string) => void;
  /** A suggestion was chosen (its canonical). */
  onSelect: (canonical: string) => void;
  /** Enter/click on the free-text row — the raw typed value, added verbatim (then linted upstream). */
  onFreeTextCommit: (raw: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
}

/**
 * Async skill autocomplete. Mirrors {@link Combobox}'s `Command` usage but drives the list from server results:
 * `shouldFilter={false}` (cmdk must not re-filter — e.g. it would hide "salesforce" for the query "sfdc"), a
 * debounced query, and a free-text fall-through so an unrecognised skill can still be added. The list is
 * absolutely positioned (rather than in a Popover) so focus stays in the input while typing.
 */
export function SkillTypeahead({
  results,
  isLoading = false,
  onQueryChange,
  onSelect,
  onFreeTextCommit,
  placeholder = 'Add a skill',
  required,
  className,
}: SkillTypeaheadProps) {
  const [focused, setFocused] = useState(false);
  const [input, setInput] = useDebouncedValue<string>(
    '',
    (q) => onQueryChange(q.trim().length >= MIN_QUERY ? q.trim() : ''),
    DEBOUNCE_MS,
  );

  const trimmed = input.trim();
  const showList = focused && trimmed.length > 0;

  const clear = () => {
    setInput('');
    onQueryChange('');
  };
  const handleSelect = (canonical: string) => {
    onSelect(canonical);
    clear();
  };
  const handleFreeText = () => {
    if (!trimmed) return;
    onFreeTextCommit(trimmed);
    clear();
  };

  return (
    <Command
      shouldFilter={false}
      className={cn('relative overflow-visible bg-transparent', className)}
    >
      <CommandInput
        value={input}
        onValueChange={setInput}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        aria-required={required}
      />
      {showList && (
        <CommandList
          // Keep focus in the input so a click's onSelect fires before onBlur closes the list.
          onMouseDown={(e) => e.preventDefault()}
          className="bg-popover text-popover-foreground absolute top-full left-0 z-50 mt-1 w-full rounded-md border shadow-md"
        >
          {isLoading && (
            <div className="text-muted-foreground px-3 py-2 text-sm">
              Searching…
            </div>
          )}
          <CommandGroup>
            {results.map((suggestion) => {
              const tone: VerdictTone = verdictTone(suggestion.band);
              return (
                <CommandItem
                  key={suggestion.canonical}
                  value={suggestion.canonical}
                  onSelect={() => handleSelect(suggestion.canonical)}
                >
                  <span
                    className={cn(
                      'mr-2 size-2 shrink-0 rounded-full',
                      TONE_DOT[tone],
                    )}
                  />
                  <span className="truncate">
                    {suggestion.display ?? suggestion.canonical}
                  </span>
                </CommandItem>
              );
            })}
            <CommandItem
              // Prefixed so the free-text row can never collide with a real suggestion's value.
              value={`__freetext__${trimmed}`}
              onSelect={handleFreeText}
            >
              <span className="mr-2 size-2 shrink-0 rounded-full border border-dashed" />
              <span className="truncate">Add &ldquo;{trimmed}&rdquo;</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      )}
    </Command>
  );
}
